<script setup lang="ts">
import {
  compareName,
  compareUsdValue,
  PortfolioAsset,
  PortfolioAssetToken,
} from '@sonarwatch/portfolio-core';
import PortfolioBodyElementBase from './PortfolioBodyElementBase.vue';
import BaseValue from '@/components/BaseValue.vue';
import BasePrice from '@/components/BasePrice.vue';
import BaseAssetFromAsset from '@/components/BaseAssetFromAsset.vue';
import BaseAmountFromAsset from '@/components/BaseAmountFromAsset.vue';
import { computed, Ref, ref } from 'vue';
import { useAppStore } from '@/store/app';
import { shortAddress } from '@/helpers/shortAddress';
import { PortfolioAssetO } from '@/types';
import { useRouter } from 'vue-router';

const appStore = useAppStore();

const router = useRouter();

const props = defineProps<{
  assets: PortfolioAssetO[];
  label: string;
  showOwner?: boolean;
}>();
const maxElements = 15;
const showAll = ref(false);

type GroupAsset = {
  assets: PortfolioAssetO[];
  uniqueAsset: PortfolioAssetToken;
};

const showTooltip: Ref<{ [key: string]: boolean }> = ref({});

const GroupedAssets = computed(() => {
  const groupedAssets = props.assets.reduce<{ [key: string]: GroupAsset }>(
    (previous: { [key: string]: GroupAsset }, currentAsset: PortfolioAsset) => {
      if (!currentAsset.data.address || !currentAsset.data.amount)
        return previous;
      const groupKey: string = currentAsset.data.address;
      const currentAssetClone = JSON.parse(JSON.stringify(currentAsset));
      if (previous[groupKey]) {
        previous[groupKey].assets.push(currentAssetClone);
        previous[groupKey].uniqueAsset.value =
          (previous[groupKey].uniqueAsset.value || 0) +
          (currentAssetClone.value || 0);
        previous[groupKey].uniqueAsset.data.amount =
          previous[groupKey].uniqueAsset.data.amount +
          currentAssetClone.data.amount;
      } else {
        previous[groupKey] = {
          assets: [currentAssetClone],
          uniqueAsset: currentAssetClone as PortfolioAssetToken,
        };
      }
      return JSON.parse(JSON.stringify(previous));
    },
    {},
  );

  const sortedGroupedAssets = Object.values(groupedAssets)
    .filter(
      (ga) =>
        ga.uniqueAsset.value &&
        ga.uniqueAsset.value >= appStore.minDisplayedValue,
    )
    .sort((a: GroupAsset, b: GroupAsset) => {
      let r = compareUsdValue(a.uniqueAsset.value, b.uniqueAsset.value);
      if (r === 0)
        r = compareName(a.uniqueAsset.data.address, b.uniqueAsset.data.address);
      return r;
    });

  if (!showAll.value && sortedGroupedAssets.length > maxElements) {
    return sortedGroupedAssets.slice(0, maxElements);
  }

  return sortedGroupedAssets;
});

const goToStake = () => {
  router.push('/stake-with-us');
};

const showStake = (address: string, amount: number) => {
  return address === '11111111111111111111111111111111' && amount > 1;
};
</script>

<template>
  <PortfolioBodyElementBase :label="label">
    <template #default>
      <v-table hover>
        <thead>
          <tr>
            <th class="text-left">Asset</th>
            <th class="text-left">Balance</th>
            <th class="text-right">Price</th>
            <th class="text-right">Value</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(groupAsset, i) in GroupedAssets" :key="i">
            <td class="text-left">
              <div class="flex items-center gap-4">
                <BaseAssetFromAsset
                  :asset="groupAsset.uniqueAsset"
                  :show-network="false"
                />
              </div>
            </td>
            <td class="text-left">
              <BaseAmountFromAsset
                :asset="groupAsset.uniqueAsset"
                :hide-label="true"
              />
              <v-tooltip
                :disabled="!showOwner"
                content-class="text-caption"
                location="right"
                v-model="showTooltip[i]"
                transition="none"
              >
                <template v-slot:activator="{ props }">
                  <v-icon
                    class="ml-1 text-disabled"
                    size="x-small"
                    icon="mdi-information"
                    v-if="showOwner"
                    v-bind="props"
                    @click="showTooltip[i] = !showTooltip[i]"
                  />
                </template>
                <p v-for="asset in groupAsset.assets">
                  <BaseAmountFromAsset :asset="asset" :hide-label="true" /> on
                  {{ shortAddress(asset.owner) }}
                </p>
              </v-tooltip>
            </td>
            <td class="text-right">
              <BasePrice :asset="groupAsset.uniqueAsset" />
            </td>
            <td class="text-right">
              <BaseValue :value="groupAsset.uniqueAsset.value" />
            </td>
          </tr>
          <tr v-if="assets.length > maxElements" @click="true">
            <th class="text-center" colspan="4">
              <v-btn
                @click="showAll = !showAll"
                block
                size="small"
                color="primary"
                variant="plain"
                class="mt-2 w-full p-0"
                :append-icon="showAll ? 'mdi-menu-up' : 'mdi-menu-down'"
              >
                {{ showAll ? 'Show less' : 'Show all' }}
              </v-btn>
            </th>
          </tr>
        </tbody>
      </v-table>
    </template>
  </PortfolioBodyElementBase>
</template>
